import { GetServerSidePropsContext, GetStaticPropsContext } from 'next'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { ReactElement, useEffect, useMemo, useState, useLayoutEffect } from 'react'
import useSWR from 'swr'
import { useRouter } from 'next/router'
import { Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import { NextPageWithLayout } from '../../common/common-types'
import { findRegionByLocale, useChinaDetector } from '../../common/region-locale'
import PageLayout from '../../components/page-layout/page-layout'
import retriveMessages, { LocaleType } from '../../messages/message'
import SceneCard, { TSceneCard } from './scene-card'
import { withSEO } from '../../common/with-seo'
import {
  getInternalOpenPlatformSolutionList,
  OpenPlatformSolutionList,
  OpenPlatformSolution,
} from '../../service/open-platform.service'

const OpenPlatform: NextPageWithLayout<{ data: OpenPlatformSolutionList }> = (props) => {
  const [solutionIndex, setSolutionIndex] = useState(0)
  const [windowWidth, setWindowWidth] = useState(0)
  const [rootFontSize, setRootFontSize] = useState(16)

  const [clientSideReady, setClientSideReady] = useState(false)

  useEffect(() => {
    const windowWidth = window.innerWidth
    setWindowWidth(windowWidth)

    // @ts-ignore
    const fontSizeStr: string = window.getComputedStyle(document.body)['font-size']
    const fontSize = Number.parseFloat(fontSizeStr)
    if (!Number.isNaN(fontSize)) {
      setRootFontSize(fontSize)
    }
  }, [])

  const t = useTranslations()
  const { locale } = useRouter()

  const slideWidth = useMemo<number>(() => {
    return windowWidth < 1200 ? 19.1875 * rootFontSize : 45 * rootFontSize
  }, [windowWidth, rootFontSize])

  const functionIcon = 'https://cdn.pudutech.com/official-website/open-platform/function-logo.svg'

  function buildFunctionIcon(iconName: string): string {
    const prefix = 'https://cdn.pudutech.com/official-website/open-platform/function/'
    return prefix + iconName
  }
  const bfi = buildFunctionIcon

  const sceneCards: TSceneCard[] = [
    {
      img: 'https://cdn.pudutech.com/official-website/open-platform/scene1.jpg',
      title: t('S1C1Title'),
      desc: t('S1C1Desc'),
      list: [
        { icon: bfi('operating-mode.svg'), desc: t('S1C1F1') },
        { icon: bfi('volume-control.svg'), desc: t('S1C1F2') },
        { icon: bfi('light-control.svg'), desc: t('S1C1F3') },
        { icon: bfi('touch-sensor-control.svg'), desc: t('S1C1F4') },
        { icon: bfi('screen.svg'), desc: t('S1C1F5') },
      ],
    },
    {
      img: 'https://cdn.pudutech.com/official-website/open-platform/scene2.jpg',
      title: t('S1C2Title'),
      desc: t('S1C2Desc'),
      list: [
        { icon: bfi('map-management.svg'), desc: t('S1C2F1') },
        { icon: bfi('destination.svg'), desc: t('S1C2F2') },
        { icon: bfi('recharge.svg'), desc: t('S1C2F3') },
        { icon: bfi('cruising.svg'), desc: t('S1C2F4') },
      ],
    },
    {
      img: 'https://cdn.pudutech.com/official-website/open-platform/scene3.jpg',
      title: t('S1C3Title'),
      desc: t('S1C3Desc'),
      list: [
        { icon: bfi('voice-scene.svg'), desc: t('S1C3F1') },
        { icon: bfi('multilingual-voice.svg'), desc: t('S1C3F2') },
        { icon: bfi('voice-delivery.svg'), desc: t('S1C3F3') },
      ],
    },
    {
      img: 'https://cdn.pudutech.com/official-website/open-platform/scene4.jpg',
      title: t('S1C4Title'),
      desc: t('S1C4Desc'),
      list: [
        { icon: bfi('delivery-data.svg'), desc: t('S1C4F1') },
        { icon: bfi('return-data.svg'), desc: t('S1C4F2') },
        { icon: bfi('cruising-data.svg'), desc: t('S1C4F3') },
        { icon: bfi('guiding-data.svg'), desc: t('S1C4F4') },
        { icon: bfi('cleaning-data.svg'), desc: t('S1C4F5') },
        { icon: bfi('battery-data.svg'), desc: t('S1C4F6') },
        { icon: bfi('abnormal-data.svg'), desc: t('S1C4F7') },
      ],
    },
  ]

  const solutionListResponse = props.data as OpenPlatformSolutionList
  const solutions: OpenPlatformSolution[] = solutionListResponse.items

  const selectedSolution = useMemo(() => {
    return solutions?.[solutionIndex]
  }, [solutionIndex, solutions])

  const Steps: string[] = [t('S3Step1'), t('S3Step2'), t('S3Step3')]

  useEffect(() => {
    setClientSideReady(true)
  }, [])

  return (
    <div className="w-full flex flex-col items-center">
      <header
        className={[
          'w-full bg-cover',
          'h-144 bg-openPlatformHeaderBgH5',
          'laptop:h-150 laptop:bg-openPlatformHeaderBg',
        ].join(' ')}
      >
        <div className="w-full h-full bg-white/30 backdrop-blur-[100px]">
          <div
            className={[
              'box-border flex justify-between items-center max-w-full w-300 h-full  mx-auto',
              'flex-col px-6 pt-12 pb-8',
              'laptop:flex-row laptop:pl-0 laptop:pr-25 laptop:pt-0 laptop:pb-0',
            ].join(' ')}
          >
            <div className="w-130 max-w-full">
              <h3 className="text-black/90 text-28 laptop:text-42 font-semibold">{t('PageTitle')}</h3>
              <p className="text-black/60 font-normal text-sm laptop:text-base mt-3 laptop:mt-6">{t('PageTip')}</p>
            </div>

            <div className="relative laptop:hidden w-175px h-186px mt-3">
              <Image
                alt="image"
                src="https://cdn.pudutech.com/official-website/open-platform/header-img_h5.png"
                className="!laptop:hidden w-175px h-186px"
                layout="fill"
                width="100%"
                height="100%"
                objectFit={'contain'}
                objectPosition={'center'}
                quality={100}
                unoptimized
              />
            </div>
            <div className="relative hidden laptop:block laptop:w-100 laptop:h-425px">
              <Image
                alt="image"
                src="https://cdn.pudutech.com/official-website/open-platform/header-img.png"
                width={400}
                height={425}
                className="!laptop:w-100 !laptop:h-425px"
              />
            </div>
          </div>
        </div>
      </header>

      <section className="w-full bg-white px-6 py-15 laptop:py-30 laptop:px-0">
        <div className="max-w-full w-300 mx-auto text-center box-border">
          <h3 className="laptop:hidden text-black/90 text-24 laptop:text-36 font-semibold">
            {t('FeatureSectionTitle')}
            {t('FeatureSectionTitle2')}
          </h3>

          <h3 className="hidden laptop:block text-black/90 text-24 laptop:text-36 font-semibold">
            {t('FeatureSectionTitle')}
          </h3>
          {t('FeatureSectionTitle2') && (
            <h3 className="hidden laptop:block text-black/90 text-24 laptop:text-36 font-semibold">
              {t('FeatureSectionTitle2')}
            </h3>
          )}
          <p className="text-black/60 laptop:w-814px text-sm laptop:text-base mt-3 laptop:mt-6 mx-auto">
            {t('FeatureSectionDesc')}
          </p>
        </div>

        <div className="max-w-full w-300 mx-auto mt-8 space-y-8 laptop:mt-16 laptop:space-y-24">
          {sceneCards?.map((item, index) => (
            <SceneCard key={index} img={item?.img} title={item?.title} desc={item?.desc} list={item?.list} />
          ))}
        </div>
      </section>

      <section className="w-full bg-pdbg py-15 laptop:px-0 laptop:py-30">
        <div className="max-w-full w-300 mx-auto px-6 laptop:px-0">
          <h3 className="text-black/90 text-24 laptop:text-36 font-semibold">{t('S2Title')}</h3>
          <p className="hidden laptop:block laptop:w-760px text-black/60 laptop:text-base laptop:mt-6">{t('S2Desc')}</p>
        </div>

        <div className="w-full mt-8 laptop:mt-16 relative">
          {clientSideReady && !!solutions?.length && (
            <Swiper
              loop={true}
              modules={[Pagination]}
              spaceBetween={8}
              slidesPerView={'auto'}
              breakpoints={{
                640: {
                  slidesPerView: 'auto',
                  spaceBetween: 8,
                },
                1280: {
                  slidesPerView: 'auto',
                  spaceBetween: 13,
                },
              }}
              slidesOffsetBefore={Math.max((windowWidth - 1200) / 2, 24)}
              pagination={{ clickable: true, el: '.scene-swiper-pagination' }}
              onSlideChange={(swiper) => setSolutionIndex(swiper.realIndex)}
              onResize={(swiper) => {
                const sideSpace = (window.innerWidth - 1200) / 2
                const slidesOffsetBefore = Math.max(sideSpace, 24)

                const slidesOffsetAfter = windowWidth - slideWidth - slidesOffsetBefore - 13
                console.log('swiper onResize')

                swiper.setTranslate(slidesOffsetBefore)
              }}
            >
              {solutions?.map((solution) => (
                <SwiperSlide key={solution.title} className="!w-307px laptop:!w-180">
                  <li className="w-fit">
                    <div className={['relative w-307px h-172px laptop:w-180 laptop:h-100'].join(' ')}>
                      <Image
                        alt={solution.title}
                        src={solution.img}
                        layout="fill"
                        width="100%"
                        height="100%"
                        objectFit={'contain'}
                        objectPosition={'center'}
                        quality={100}
                        unoptimized
                      />
                    </div>
                  </li>
                </SwiperSlide>
              ))}

              <div slot="container-end" className="max-w-full w-300 mx-auto mt-8 laptop:mt-12 px-6 laptop:px-0">
                <div className="scene-swiper-pagination pd-pagination-container_left pd-pagination-container_black"></div>
              </div>
            </Swiper>
          )}
        </div>

        <div className="max-w-full w-300 mx-auto mt-6 laptop:mt-6 px-6 laptop:px-0">
          <div className="max-w-full laptop:w-760px">
            <h5 className="text-black/90 text-21 laptop:text-24 font-semibold">{selectedSolution?.title}</h5>
            <p className="text-black/60 text-base laptop:text-sm mt-3 laptop:mt-4 font-normal">
              {selectedSolution?.desc}
            </p>
          </div>
        </div>
      </section>

      <section className="w-full bg-white py-15 laptop:py-30">
        <div className="flex flex-col items-start laptop:items-center max-w-full w-300 mx-auto px-6 laptop:px-0">
          <div className="relative laptop:w-20 laptop:h-20 w-15 h-15">
            <Image
              alt="join our ecosystem"
              src="https://cdn.pudutech.com/official-website/open-platform/join.svg"
              layout="fill"
              width="100%"
              height="100%"
              objectFit={'contain'}
              objectPosition={'center'}
              quality={100}
              unoptimized
            />
          </div>
          <h3 className="text-black/90 text-24 laptop:text-36 font-semibold">{t('S3Title')}</h3>
          <ul className="w-full laptop:w-fit flex flex-col laptop:flex-row justify-between mt-8 laptop:mt-12 gap-y-18px laptop:gap-y-0 gap-x-0 laptop:gap-x-6">
            {Steps.map((step) => (
              <li
                key={step}
                className=" bg-[#f2f3f5] text-black/90 text-18 laptop:text-20 p-3 laptop:p-6 font-semibold"
              >
                {step}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  )
}

export const getServerSideProps = withSEO(
  async ({ locale, query, req }: GetServerSidePropsContext) => {
    const messages = retriveMessages(['open-platform'], locale as LocaleType)

    const data: OpenPlatformSolutionList = { items: [] }

    const region = findRegionByLocale(locale as string)
    try {
      const resp: OpenPlatformSolutionList = await getInternalOpenPlatformSolutionList(region)
      data.items = resp?.items
      console.log('===========OpenPlatformSolutionList-RES===========', resp)
    } catch (err) {
      console.log('===========OpenPlatformSolutionList-ERR===========', err)
    }

    return {
      props: {
        data,
        messages,
      },
    }
  },
  () => '/open-platform'
)

OpenPlatform.getLayout = function (page: ReactElement, seo) {
  return (
    <PageLayout
      sticky={true}
      pageTitle={seo.title}
      pageDescription={seo.description}
      pageKeywrods={seo.keywords}
      ogtype={'website'}
    >
      {page}
    </PageLayout>
  )
}

export default OpenPlatform
